/* eslint-disable react-hooks/exhaustive-deps */
import MainLayout from '../../../../../../components/layouts/MainLayout';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import { FaTrashAlt } from 'react-icons/fa';
import { RiBarcodeLine } from 'react-icons/ri';
import { FormInput, Message, Loader, theme } from '@omnigenbiodata/ui';
import { useAppDispatch, useAppSelector } from '../../../../../../store';
import { DataGrid, GridAlignment } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import ROUTES from '../../../../../../core/constants/routes.constants';
import {
  isBusySelector,
  errorSelector,
  responseSelector,
  manifestResponseSelector,
} from '../../../../../../store/shipping/selectors';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { FaUndo } from 'react-icons/fa';
import { useFormik } from 'formik';
import Buttons from '../../../../../../components/forms/Buttons';
import CircularProgress from '@mui/material/CircularProgress';
import ScanEvent from '../../../../../../components/forms/ScanEvent';
import {
  getShippingThunk,
  updateShippingThunk,
  getStagedManifestThunk,
  stagedManifestResponseSelector,
  stagedManifestIsBusySelector,
  dispatchErrorSelector,
  dispatchResponseSelector,
  dispatchIsBusySelector,
  getShipmentThunk,
  getShipmentManifestThunk,
} from '../../../../../../store/shipping';
import {
  boxResponseSelector,
  boxErrorSelector,
  boxIsBusySelector,
  getStorageBoxThunk,
  reset,
} from '../../../../../../store/storage';
import ConfirmationModal from './components/ConfirmationModal';
import { CSVLink } from 'react-csv';
import { AMGEN_DOT_COLOUR, DATE_FORMAT, DATE_VALUE_FORMAT } from '../../../../../../core/constants/forms.constants';
import { useParams } from 'react-router-dom';
import moment from 'moment';

const shelfComparator = (v1: string, v2: string) => Number(v1) - Number(v2);
const columns = (deleteBox: (boxBarcode: string) => void, status: string) => [
  {
    field: 'barcode',
    headerName: 'Box',
    flex: 1,
  },
  {
    field: 'sequencer',
    headerName: 'Sequencer',
    align: 'left' as GridAlignment,
    flex: 1,
    renderCell: (params: any) => {
      let dotColor = '';
      if (params.value === 'REGENERON') {
        dotColor = theme.palette.info.main;
      } else if (params.value === 'AMGEN') {
        dotColor = AMGEN_DOT_COLOUR;
      } else {
        dotColor = theme.palette.grey[600];
      }

      return (
        <Box display="flex" alignItems="center">
          {dotColor && (
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: dotColor,
                marginRight: 1,
              }}
            />
          )}
          <Typography>{params.value === null ? 'Unassigned' : params.value}</Typography>
        </Box>
      );
    },
  },
  {
    field: 'shelfPosition',
    headerName: 'Shelf',
    flex: 0.5,
    sortComparator: shelfComparator,
  },
  {
    field: 'count',
    headerName: 'Samples',
    flex: 0.5,
    align: 'center' as GridAlignment,
    headerAlign: 'center' as GridAlignment,
  },
  ...(status === 'STAGED'
    ? [
        {
          field: 'delete',
          headerName: '',
          align: 'right' as GridAlignment,
          flex: 0.5,
          renderCell: ({ id }: any) => (
            <IconButton
              aria-label="Delete Box"
              data-testid={`delete-box-${id}`}
              size="small"
              onClick={() => {
                deleteBox(id);
              }}
            >
              <FaTrashAlt fontSize={20} />
            </IconButton>
          ),
        },
      ]
    : []),
];

function CreateShipmentScene() {
  const { t } = useTranslation('portal');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const errors = useAppSelector(errorSelector);
  const staged = useAppSelector(responseSelector);
  const isBusy = useAppSelector(isBusySelector);
  const box = useAppSelector(boxResponseSelector);
  const boxError = useAppSelector(boxErrorSelector);
  const boxBusy = useAppSelector(boxIsBusySelector);
  const dispatchError = useAppSelector(dispatchErrorSelector);
  const dispatchIsBusy = useAppSelector(dispatchIsBusySelector);
  const dispatchResponse = useAppSelector(dispatchResponseSelector);
  const stagedManifest = useAppSelector(stagedManifestResponseSelector);
  const finalManifest = useAppSelector(manifestResponseSelector);
  const manifestLoading = useAppSelector(stagedManifestIsBusySelector);
  const [showConfirm, setConfirm] = useState(false);
  const stagedManifestDownload = useRef<any>(null);
  const { shippingId } = useParams<any>();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      boxBarcode: '',
      shippingDate: moment().format(DATE_VALUE_FORMAT),
    },
    onSubmit: () => {},
  });

  useEffect(() => {
    if (!shippingId) {
      dispatch(getShippingThunk());
    }
  }, [dispatch]);

  useEffect(() => {
    if (box && formik.values.boxBarcode) {
      if (box.samples.some((sample) => sample.participantWithdrawn)) {
        navigate(ROUTES.storageEdit.replace(':boxBarcode', box.boxBarcode), {
          state: { shipping: 'new' },
        });
      } else {
        dispatch(updateShippingThunk({ boxBarcode: box.boxBarcode, operation: 'add' }));
        dispatch(reset());
      }
      formik.setFieldValue('boxBarcode', '');
    }
  }, [box, dispatch, navigate, formik]);

  useEffect(() => {
    if (dispatchResponse) {
      navigate(ROUTES.shippingSuccess);
    }
  }, [dispatchResponse, navigate]);

  useEffect(() => {
    if (stagedManifest) {
      stagedManifestDownload.current.link.click();
    }
  }, [stagedManifest]);

  useEffect(() => {
    if (finalManifest) {
      window.open(finalManifest.link, '_blank');
    }
  }, [finalManifest]);

  useEffect(() => {
    if (shippingId) {
      dispatch(getShipmentThunk({ shipmentId: shippingId }));
    }
  }, [dispatch, shippingId]);

  const deleteBox = (boxBarcode: string) => {
    dispatch(updateShippingThunk({ boxBarcode: boxBarcode, operation: 'remove' }));
  };

  const downloadManifest = () => {
    if (staged?.sampleBoxes?.length && (staged?.status === 'STAGED' || staged?.status === '')) {
      dispatch(getStagedManifestThunk());
    } else if (staged?.shipmentId) {
      dispatch(getShipmentManifestThunk(staged?.shipmentId));
    }
  };

  const handleScan = (boxBarcode: string) => {
    formik.setFieldValue('boxBarcode', boxBarcode);
    dispatch(getStorageBoxThunk(boxBarcode));
  };

  const handleConfirm = () => {
    if (staged?.sampleBoxes?.length) {
      setConfirm(true);
    }
  };

  let dotColor = '';
  if (staged?.sequencer === 'REGENERON') {
    dotColor = theme.palette.info.main;
  } else if (staged?.sequencer === 'AMGEN') {
    dotColor = AMGEN_DOT_COLOUR;
  } else {
    dotColor = theme.palette.grey[600];
  }

  const getDownloadManifestLabel = () => {
    const buttonLabel = {
      CURRENT: 'buttons.downloadDraftManifest',
      STAGED: 'buttons.downloadDraftManifest',
      MANIFESTED: 'buttons.downloadInterimManifest',
      SHIPPED: 'buttons.downloadFinalManifest',
    };

    const status = staged?.status as keyof typeof buttonLabel; // Typecast it to a valid key of `buttonLabel`

    return buttonLabel[status] || buttonLabel['CURRENT']; // Default to 'CURRENT' if status is invalid or undefined
  };

  return (
    <MainLayout title={t('createShipment.title')}>
      <Container maxWidth="sm">
        <Typography mb={2} variant="body1" align="center">
          {t('createShipment.text')}
        </Typography>
        {stagedManifest && (
          <CSVLink
            filename="DMUK Draft Manifest.csv"
            data={stagedManifest}
            target="_blank"
            data-testid="draft-manifest-download"
            ref={stagedManifestDownload}
          />
        )}

        {!shippingId && (
          <form onSubmit={formik.handleSubmit}>
            <ScanEvent data-testid="scanner" onScan={handleScan} />
            <Grid container py={2} justifyContent="center">
              <Grid item xs={12} sm={6}>
                <FormInput
                  label={t('forms.boxBarcode')}
                  type="text"
                  name={`boxBarcode`}
                  data-testid="boxBarcode"
                  error={formik.errors.boxBarcode}
                  touched={formik.touched.boxBarcode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.boxBarcode}
                  readOnly
                  disabled={!formik.values?.boxBarcode}
                  margin="none"
                  startAdornment={<RiBarcodeLine fontSize={25} />}
                  endAdornment={
                    formik.values.boxBarcode && (
                      <IconButton
                        aria-label="delete"
                        size="small"
                        onClick={() => {
                          formik.setFieldValue('boxBarcode', '');
                        }}
                      >
                        <FaUndo fontSize={20} color={theme.palette.error.dark} />
                      </IconButton>
                    )
                  }
                />
              </Grid>
            </Grid>
          </form>
        )}
        {errors && <Message severity="error">{errors[0].message}</Message>}
        {dispatchError && <Message severity="error">{dispatchError[0].message}</Message>}
        {boxError && <Message severity="error">{boxError[0].message}</Message>}
      </Container>

      <Grid container alignItems="center" justifyContent="space-between" spacing={2} sx={{ width: '100%', mt: 2 }}>
        <Grid item xs={12} sm="auto">
          {staged !== null && (
            <Box display="flex" alignItems="center" mb={2} sx={{ mb: 0 }}>
              <Typography sx={{ marginRight: 1 }}>
                <strong>Sequencer:</strong>
              </Typography>
              <Box display="flex" alignItems="center">
                {dotColor && (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: dotColor,
                      marginRight: 1,
                    }}
                  />
                )}
                <Typography data-testid="sequencer_value">
                  {staged?.sequencer === null ? 'Unassigned' : staged?.sequencer}
                </Typography>
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} sm="auto" sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
          <Button
            size="large"
            sx={{
              paddingX: 6,
              minWidth: '210px',
              minHeight: '42px',
            }}
            disabled={!staged?.sampleBoxes?.length}
            variant="contained"
            onClick={downloadManifest}
          >
            {manifestLoading ? (
              <CircularProgress data-testid="export-loader" size={20} sx={{ color: 'white' }} />
            ) : (
              t(getDownloadManifestLabel())
            )}
          </Button>
        </Grid>
      </Grid>
      {staged?.status && (
        <Typography sx={{ marginRight: 1 }}>
          <strong>Status:</strong>{' '}
          {staged?.status === 'STAGED' && (
            <Typography component="span" sx={{ color: theme.palette.error.main }}>
              {staged?.status === 'STAGED' ? 'CURRENT' : staged?.status}
            </Typography>
          )}
          {staged?.status === 'MANIFESTED' && (
            <Typography component="span" sx={{ color: AMGEN_DOT_COLOUR }}>
              {staged?.status}
            </Typography>
          )}
          {staged?.status === 'SHIPPED' && (
            <Typography component="span" sx={{ color: theme.palette.success.main }}>
              {staged?.status}
            </Typography>
          )}
        </Typography>
      )}

      {staged?.status === 'SHIPPED' && (
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ width: '100%', mt: 0, mb: 0 }}
        >
          <Grid item xs={12} sm={4}>
            <Box display="flex" alignItems="center" mb={2} sx={{ mb: 0 }}>
              <strong>Shipped Date:&nbsp;</strong> {moment(staged?.shippedDate).format(DATE_FORMAT)}
            </Box>
          </Grid>
        </Grid>
      )}

      <Box mb={1} mt={2} sx={{ bgcolor: 'background.paper' }}>
        <DataGrid
          loading={isBusy || boxBusy}
          rows={staged?.sampleBoxes || []}
          autoHeight
          getRowId={(row) => row.barcode}
          columns={columns(deleteBox, staged?.status || 'STAGED')}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
        />
      </Box>
      {staged?.status === 'MANIFESTED' && (
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ width: '100%', mt: 0, mb: 4 }}
        >
          <Grid item xs={12} sm={4}>
            <Box display="flex" alignItems="center" mb={2} sx={{ mb: 0 }}>
              <FormInput
                error={formik.errors.shippingDate}
                showErrorMessage={false}
                touched={formik.touched.shippingDate}
                name={`shippingDate`}
                label="Shipping Date"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                type="date"
                value={formik.values.shippingDate}
                inputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          </Grid>
        </Grid>
      )}

      {staged?.status !== 'SHIPPED' && (
        <>
          {shippingId ? (
            <Buttons
              isDisabled={!staged?.sampleBoxes?.length}
              forwardLabel={t('buttons.ship')}
              onForward={handleConfirm}
            />
          ) : (
            <Buttons
              isDisabled={!staged?.sampleBoxes?.length}
              forwardLabel={t('buttons.markManifested')}
              onForward={handleConfirm}
            />
          )}
        </>
      )}

      <ConfirmationModal
        isOpen={showConfirm}
        onClose={() => setConfirm(false)}
        staged={staged}
        t={t}
        shipmentDate={formik.values.shippingDate}
      />
      <Loader isVisible={dispatchIsBusy} label={t('loaders.shipping') as string} />
    </MainLayout>
  );
}

export default CreateShipmentScene;
